"use client";

import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { QuantityField } from "@/components/tools/physics-mechanics/quantity-field";
import { Working } from "@/components/tools/physics-mechanics/working";
import { t } from "@/lib/i18n/interpolate";
import {
  type CircularError,
  type CircularMotion,
  circularMotion,
  GIVEN_FAMILIES,
  type GivenMotion,
} from "@/lib/tool-kit/physics-mechanics/circular";
import {
  formatPlain,
  formatSig,
  type NumberFormatter,
  withUnit,
} from "@/lib/tool-kit/physics-mechanics/format";
import { parseQuantity } from "@/lib/tool-kit/physics-mechanics/quantity";
import {
  ACCELERATION,
  fromBase,
  LENGTH,
  STANDARD_GRAVITY,
  toBase,
  type UnitFamily,
  unitOptions,
  unitSymbol,
} from "@/lib/tool-kit/physics-mechanics/units";

export interface CircularState {
  given: GivenMotion;
  value: string;
  unit: string;
  radius: string;
  radiusUnit: string;
}

export interface CircularStrings {
  givenLabel: string;
  givenSpeed: string;
  givenOmega: string;
  givenPeriod: string;
  givenFrequency: string;
  radius: string;
  radiusHint: string;
  unitFor: string;
  omega: string;
  period: string;
  frequency: string;
  rpm: string;
  speed: string;
  acceleration: string;
  accelerationG: string;
  workingHeading: string;
  stepOmega: string;
  stepPeriod: string;
  stepSpeed: string;
  stepAcceleration: string;
  copy: string;
  copied: string;
  copyText: string;
  circularNeed: string;
  circularUnreadable: string;
  circularErrors: Record<CircularError, string>;
}

/** The id of a family's SI base unit (factor 1), or its first unit. */
function baseUnitId(family: UnitFamily): string {
  return (family.units.find((unit) => unit.factor === 1) ?? family.units[0]).id;
}

const GIVEN_KEYS: readonly GivenMotion[] = ["v", "w", "T", "f"];

export function CircularInputs({
  state,
  onState,
  strings,
}: {
  state: CircularState;
  onState: (state: CircularState) => void;
  strings: CircularStrings;
}) {
  const givenLabels: Record<GivenMotion, string> = {
    v: strings.givenSpeed,
    w: strings.givenOmega,
    T: strings.givenPeriod,
    f: strings.givenFrequency,
  };
  const family = GIVEN_FAMILIES[state.given];
  return (
    <>
      <PillGroup
        label={strings.givenLabel}
        name="cf-given"
        onChange={(given) =>
          onState({
            ...state,
            given,
            unit: baseUnitId(GIVEN_FAMILIES[given]),
          })
        }
        options={GIVEN_KEYS.map((key) => ({
          value: key,
          label: givenLabels[key],
        }))}
        value={state.given}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <QuantityField
          id="cf-given-value"
          label={givenLabels[state.given]}
          onUnit={(unit) => onState({ ...state, unit })}
          onValue={(value) => onState({ ...state, value })}
          unit={state.unit}
          unitLabel={t(strings.unitFor, { name: givenLabels[state.given] })}
          units={unitOptions(family)}
          value={state.value}
        />
        <QuantityField
          hint={strings.radiusHint}
          id="cf-radius"
          label={strings.radius}
          onUnit={(radiusUnit) => onState({ ...state, radiusUnit })}
          onValue={(radius) => onState({ ...state, radius })}
          unit={state.radiusUnit}
          unitLabel={t(strings.unitFor, { name: strings.radius })}
          units={unitOptions(LENGTH)}
          value={state.radius}
        />
      </div>
    </>
  );
}

function circularSteps(
  motion: CircularMotion,
  radius: number | null,
  strings: CircularStrings,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const p = (value: number) => formatPlain(fmt, value);
  const r = (value: number) => formatSig(fmt, value, sig);
  const steps = [
    t(strings.stepOmega, { omega: r(motion.omega), rpm: r(motion.rpm) }),
    t(strings.stepPeriod, {
      omega: p(motion.omega),
      period: r(motion.period),
      frequency: r(motion.frequency),
    }),
  ];
  if (
    radius !== null &&
    motion.speed !== null &&
    motion.acceleration !== null
  ) {
    steps.push(
      t(strings.stepSpeed, {
        omega: p(motion.omega),
        radius: p(radius),
        speed: r(motion.speed),
      })
    );
    steps.push(
      t(strings.stepAcceleration, {
        omega: p(motion.omega),
        radius: p(radius),
        acceleration: r(motion.acceleration),
      })
    );
  }
  return steps;
}

export function CircularResults({
  state,
  strings,
  fmt,
  sig,
}: {
  state: CircularState;
  strings: CircularStrings;
  fmt: NumberFormatter;
  sig: number;
}) {
  if (state.value.trim() === "") {
    return <Notice>{strings.circularNeed}</Notice>;
  }
  const value = parseQuantity(state.value);
  const radius =
    state.radius.trim() === "" ? null : parseQuantity(state.radius);
  if (value === null || (state.radius.trim() !== "" && radius === null)) {
    return <ErrorNotice>{strings.circularUnreadable}</ErrorNotice>;
  }
  const radiusSi =
    radius === null ? null : toBase(radius, LENGTH, state.radiusUnit);
  const motion = circularMotion(
    state.given,
    toBase(value, GIVEN_FAMILIES[state.given], state.unit),
    radiusSi
  );
  if ("error" in motion) {
    return <ErrorNotice>{strings.circularErrors[motion.error]}</ErrorNotice>;
  }
  const r = (number: number) => formatSig(fmt, number, sig);
  const speedUnit = "m/s";
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.omega}
          value={withUnit(r(motion.omega), "rad/s")}
        />
        <StatCard
          emphasis
          label={strings.period}
          value={withUnit(r(motion.period), "s")}
        />
        <StatCard
          emphasis
          label={strings.frequency}
          value={withUnit(r(motion.frequency), "Hz")}
        />
        <StatCard label={strings.rpm} value={withUnit(r(motion.rpm), "rpm")} />
        {motion.speed === null ? null : (
          <StatCard
            label={strings.speed}
            value={withUnit(r(motion.speed), speedUnit)}
          />
        )}
        {motion.acceleration === null ? null : (
          <StatCard
            label={strings.acceleration}
            value={withUnit(
              r(fromBase(motion.acceleration, ACCELERATION, "mps2")),
              unitSymbol(ACCELERATION, "mps2")
            )}
          />
        )}
        {motion.acceleration === null ? null : (
          <StatCard
            label={strings.accelerationG}
            value={r(motion.acceleration / STANDARD_GRAVITY)}
          />
        )}
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={t(strings.copyText, {
          omega: withUnit(r(motion.omega), "rad/s"),
          period: withUnit(r(motion.period), "s"),
          frequency: withUnit(r(motion.frequency), "Hz"),
        })}
      />
      <Working
        heading={strings.workingHeading}
        steps={circularSteps(motion, radiusSi, strings, fmt, sig)}
      />
    </>
  );
}
